import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Clock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Brand } from "@/components/bits";
import { ThemeToggle } from "@/components/ThemeToggle";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_SECONDS = 60;

export default function Signup() {
  const [step, setStep] = useState("details"); // details | code | pending
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [resendBusy, setResendBusy] = useState(false);
  const [pendingMsg, setPendingMsg] = useState(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitDetails = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    if (name.length < 2) return setError("Enter your full name.");
    if (!EMAIL_RE.test(email)) return setError("Enter a valid business email.");
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/signup", { name, email, password: form.password });
      setForm((f) => ({ ...f, name, email }));
      if (data?.dev_code) setDevCode(data.dev_code);
      setStep("code");
      setCooldown(RESEND_SECONDS);
      toast.success(`Verification code sent to ${data?.email || email}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(err.response?.status === 409 ? detail || "An account with this email already exists — sign in instead." : detail || "Could not create the account. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resendBusy) return;
    setResendBusy(true);
    try {
      const { data } = await api.post("/auth/resend-code", { email: form.email.trim() });
      if (data?.dev_code) setDevCode(data.dev_code);
      setCooldown(RESEND_SECONDS);
      toast.success("New code sent — check your inbox");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend the code");
    } finally {
      setResendBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return setError("Enter the 6-digit code from your email.");
    setVerifying(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/verify", { email: form.email.trim(), code });
      if (data?.status === "active" && data.token) {
        try {
          localStorage.setItem("av_token", data.token);
        } catch (err) {
          /* localStorage unavailable — cookie session still applies */
        }
        toast.success("Email verified — welcome to AgriVoice");
        window.location.href = "/dashboard";
        return;
      }
      if (data?.status === "pending_approval") {
        setPendingMsg(data.detail || null);
        setStep("pending");
        return;
      }
      setError(data?.detail || "That code did not work — check it and try again.");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired code — try again or resend a new one.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#080C0A] grain flex flex-col">
      <div className="absolute top-4 right-4"><ThemeToggle /></div>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block text-[11px] font-mono text-slate-500 hover:text-emerald-300 mb-6" data-testid="signup-back-home">← Back to home</Link>

          <div className="glass p-7 sm:p-8 rise" data-testid="signup-card">
            <div className="flex items-center gap-3 mb-7">
              <Brand size="lg" />
            </div>

            {step !== "pending" && (
              <div className="flex items-center gap-2 mb-6" data-testid="signup-stepper">
                {["Account details", "Verify email"].map((s, i) => {
                  const idx = step === "details" ? 0 : 1;
                  const done = i < idx;
                  const active = i === idx;
                  return (
                    <div key={s} className="flex items-center gap-2 flex-1">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono ${done ? "bg-emerald-500 text-[#06110A]" : active ? "border border-emerald-400 text-emerald-300" : "border border-white/10 text-slate-600"}`}>{i + 1}</div>
                      <span className={`text-xs ${active ? "text-slate-100" : "text-slate-500"}`}>{s}</span>
                      {i === 0 && <div className={`flex-1 h-px ${done ? "bg-emerald-500/60" : "bg-white/10"}`} />}
                    </div>
                  );
                })}
              </div>
            )}

            {step === "details" && (
              <>
                <p className="overline-label mb-2">Step 1 · Account details</p>
                <h1 className="font-display text-2xl font-bold text-slate-50">Create your AgriVoice account</h1>
                <p className="text-sm text-slate-400 mt-1.5">Use your business email — we'll send a 6-digit verification code.</p>
                <form onSubmit={submitDetails} className="mt-6 space-y-4" data-testid="signup-form">
                  <div>
                    <label className="field-label" htmlFor="signup-name">Full name</label>
                    <input id="signup-name" className="field" placeholder="e.g. Anita Deshmukh" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="signup-name" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="signup-email">Business email</label>
                    <input id="signup-email" type="email" autoComplete="email" className="field" placeholder="you@company.com" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="signup-email" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="field-label" htmlFor="signup-password">Password</label>
                      <input id="signup-password" type="password" autoComplete="new-password" className="field" placeholder="8+ characters" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="signup-password" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="signup-confirm">Confirm password</label>
                      <input id="signup-confirm" type="password" autoComplete="new-password" className="field" placeholder="Repeat password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} data-testid="signup-confirm" />
                    </div>
                  </div>

                  {error && <p className="text-xs text-rose-300 bg-rose-950/40 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="signup-error">{error}</p>}

                  <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="signup-submit">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</> : <>Create account <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              </>
            )}

            {step === "code" && (
              <>
                <p className="overline-label mb-2">Step 2 · Verify email</p>
                <h1 className="font-display text-2xl font-bold text-slate-50">Enter the 6-digit code</h1>
                <p className="text-sm text-slate-400 mt-1.5">We sent it to <span className="text-slate-200">{form.email}</span>.</p>
                <form onSubmit={verify} className="mt-6 space-y-4" data-testid="signup-verify-form">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="••••••"
                    className="field text-center font-mono text-2xl tracking-[0.5em] pl-[0.5em]"
                    data-testid="signup-code-input"
                  />

                  {devCode && (
                    <button type="button" onClick={() => setCode(devCode)} className="w-full rounded-lg border border-amber-500/30 bg-amber-950/40 px-3 py-2 text-left text-[11px] font-mono text-amber-300 hover:border-amber-500/50" data-testid="signup-dev-code">
                      dev mode · your code is <span className="font-semibold">{devCode}</span> — tap to fill
                    </button>
                  )}

                  {error && <p className="text-xs text-rose-300 bg-rose-950/40 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="signup-error">{error}</p>}

                  <button type="submit" disabled={verifying || code.length !== 6} className="btn-primary w-full" data-testid="signup-verify-btn">
                    {verifying ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : "Verify & continue"}
                  </button>

                  <div className="flex items-center justify-between text-[11px] font-mono">
                    <button type="button" onClick={resend} disabled={resendBusy || cooldown > 0} className="text-emerald-300 hover:text-emerald-200 disabled:text-slate-500" data-testid="signup-resend-btn">
                      {resendBusy ? "sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                    </button>
                    <button type="button" onClick={() => { setStep("details"); setError(null); }} className="text-slate-500 hover:text-slate-200" data-testid="signup-back-btn">← Change details</button>
                  </div>
                </form>
              </>
            )}

            {step === "pending" && (
              <div className="text-center py-3" data-testid="signup-pending">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 mx-auto mb-4 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-300" /></div>
                <h2 className="font-display text-xl font-bold text-slate-50">Account created — waiting for approval</h2>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed max-w-sm mx-auto">
                  {pendingMsg || "Contact an AgriVoice admin at agrivoice.co.in to activate your account. Once they do, sign in with your email and password."}
                </p>
                <Link to="/login" className="btn-primary mt-6" data-testid="signup-pending-login">Back to sign in</Link>
              </div>
            )}
          </div>

          {step !== "pending" && (
            <p className="text-center text-sm text-slate-500 mt-5">
              Already have an account? <Link to="/login" className="text-emerald-300 hover:text-emerald-200" data-testid="signup-login-link">Sign in</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
